import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericGet } from '@/_shared/types';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_DEPARTMENTS_DEPARTMENTID_YEAR_YEAR_QUARTER_QUARTER_PATH_KEY } from '@/swagger/constants/credits-keys';
import { API_GET_DEPARTMENTS_CREDITS, KEY_GET_DEPARTMENTS_CREDITS } from '../constants';
import { MOCK_RESPONSE_GET_DEPARTMENTS_CREDITS } from '../mocks';

type RequestGetDepartmentsCredits = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_DEPARTMENTS_DEPARTMENTID_YEAR_YEAR_QUARTER_QUARTER_PATH_KEY
>['request']['path'];

export type ResponseGetDepartmentsCredits = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_DEPARTMENTS_DEPARTMENTID_YEAR_YEAR_QUARTER_QUARTER_PATH_KEY
>['response']['body'];

export type CallParamsGetDepartmentsCredits = Omit<
  RequestGetDepartmentsCredits,
  'organizationId'
> & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponseGetDepartmentsCredits = ResponseGetDepartmentsCredits;

type CallErrorGetDepartmentsCredits = {
  message: string;
  code: number;
};

type CallSelectorsGetDepartmentsCredits = {
  stats: CallResponseGetDepartmentsCredits['stats'];
  clusterUsers: CallResponseGetDepartmentsCredits['stats']['clusterUsers'];
  teams: CallResponseGetDepartmentsCredits['teams'];
  hasNoData: boolean;
};

export const getDepartmentsCreditsCall = createGeneralCall<
  CallParamsGetDepartmentsCredits,
  CallResponseGetDepartmentsCredits,
  CallErrorGetDepartmentsCredits,
  CallSelectorsGetDepartmentsCredits
>({
  id: KEY_GET_DEPARTMENTS_CREDITS,
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_DEPARTMENTS_CREDITS(
        org as unknown as string,
        payload.departmentId,
        payload.year,
        payload.quarter,
      );
    },
  paramsSelector:
    ({ payload }) =>
    () => {
      return payload;
    },
  method: 'get',
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_DEPARTMENTS_CREDITS : undefined,
  selectors: {
    stats: ({ selectors }) => createSelector(selectors.response, res => res?.stats),
    clusterUsers: ({ selectors }) =>
      createSelector(selectors.response, res => res?.stats?.clusterUsers || []),
    teams: ({ selectors }) => createSelector(selectors.response, res => res?.teams || []),
    hasNoData: ({ selectors }) =>
      createSelector(
        selectors.response,
        res =>
          res?.stats?.assigned === 0 &&
          res?.teams?.length === 0 &&
          res?.stats?.clusterUsers?.length === 0,
      ),
  },
});
